/**************************************************************************
 * Created on Tue Feb 11 2026
 *
 *  # Honeywell Code Usage Policy
 *
 * **Honeywell Source Code Usage Policy Classification:** Highly Confidential
 *
 * [comment]: # (Options for above are "Inner Source", "General Application", "Highly Confidential", "Export Controlled")
 *
 * **This repository has multiple classifications:** Yes
 *
 * [comment]: # (Options for above are "Yes" when subfolders contain a separate policy,"No" when the whole repo has the same policy)
 *
 * **Honeywell confidential - For internal use only.
 *
 * Property of and copyright ownership by Honeywell International Inc. and/or its affiliates.
 *
 * Use of source code must follow all Honeywell policies including the [HCE Source Code Access Policy](http://go.honeywell.com/hce-code-access-policy).
 *
 * External use or distribution prohibited without written approval per Honeywell policies**
 *
********************************************************************************/

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { setCookie, ID_TOKEN_KEY } from "../../../interceptors/constant";
import DSSLoader from "../../../ui/building-blocks/atoms/dss-loader/dss-loader";
import { notifyError } from "../../../ui/dls/dls-snackbar/dls-snackbar";
import { SESSION_REQUEST_CONFIG, TOKEN_URL_GUEST } from "../../constant/constant";
import GuestRouter from "../../../app-router/guest-router";
import { useDssContext } from "../../../DSSContext";
import { getGuestTokenDetails } from "../../../modules/download/api/download-database-api";
import GuestErrorView from "../guest-error-view/guest-error-view";

const GuestLogin = () => {
    const location = useLocation();
    const useQuery = () => {
        const searchString = location.search.replace(/\+/g, '%2B');
        return new URLSearchParams(searchString);
    };
    
    const navigate = useNavigate();
    const { setGuestTokenData } = useDssContext();
    const [code] = useState(useQuery().get('token'));
    const [authenticated, setAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLinkExpired, setIsLinkExpired] = useState(false);
    const hasStartedRef = useRef(false);
    const lastProcessedTokenRef = useRef<string | null>(null);
    const storefront = "INDS";

    const startLoginProcess = async () => {
        try {
            const resp = await axios.post(`${TOKEN_URL_GUEST}?code=${code}&storefrontName=${storefront}&appName=${storefront}&storefront=${storefront}`,undefined, SESSION_REQUEST_CONFIG);
            if (resp.data?.authenticated === false) {
                throw new Error('Failed to establish authenticated session');
            }
            setCookie(ID_TOKEN_KEY, resp.data?.user_id ?? code, resp.data?.expires_in ?? 3600);
            
            if (code) {
                try {
                    // await getGuestTokenDetails({ token: code, validateToken: "Y" });
                    const tokenData = await getGuestTokenDetails({ token: code, validateToken: "Y" });
                    setGuestTokenData(tokenData);
                } catch (error: any) {
                    sessionStorage.removeItem('isGuestUser');
                    setIsLinkExpired(true);
                    setAuthenticated(false);
                    return;
                }
            }
            
            sessionStorage.setItem('isGuestUser', 'true');
            setAuthenticated(true);
        } catch (e: any) {
            sessionStorage.removeItem('isGuestUser');
            setIsLinkExpired(true);
            setAuthenticated(false);
            notifyError(e.message || 'Failed to get products');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!code) {
            notifyError('No token provided');
            sessionStorage.removeItem('isGuestUser');
            navigate('/prelogin', { replace: true });
        } else {
            if (hasStartedRef.current && lastProcessedTokenRef.current === code) {
                return;
            }
            hasStartedRef.current = true;
            lastProcessedTokenRef.current = code;
            startLoginProcess();
        }
        return () => {
            if (isLinkExpired) {
                sessionStorage.removeItem('isGuestUser');
            }
        };
    }, [code, navigate]);

    if (isLoading) {
        return (
            <div style={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DSSLoader id="guest-loader" />
            </div>
        );
    }

    if (!authenticated) {
        return <GuestErrorView isExpiredLinkError={isLinkExpired} />;
    }

    return <GuestRouter />;
};

export default GuestLogin;
